import { useState, useMemo } from 'react';
import { Trans, Plural, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Button, Card, CardContent } from '@pantry/ui';
import { cn } from '@pantry/ui';
import { useProductGroup, useItems, type ItemWithInstanceCounts } from '@pantry/api-client';
import { useSelectedHousehold } from '../../contexts/HouseholdContext';
import { UNIT_DISPLAY_LABELS, type Product, getDaysUntilExpiry } from '@pantry/shared';
import { useResolveProductName } from '../../hooks/useProductLanguage';
import { SetTargetDialog } from '../../components/inventory/SetTargetDialog';
import {
  ArrowLeftIcon,
  PencilIcon,
  ArchiveBoxIcon,
  HomeIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

export const Route = createFileRoute('/_app/items_/groups/$groupId')({
  component: GroupDetailPage,
});

// ============================================================================
// MemberItemRow sub-component
// ============================================================================

interface MemberItemRowProps {
  item: ItemWithInstanceCounts;
}

function MemberItemRow({ item }: MemberItemRowProps) {
  const { t } = useLingui();
  const resolveProductName = useResolveProductName();
  const itemName =
    item.customName ||
    (item.product ? resolveProductName(item.product as Product) : null) ||
    item.product?.name ||
    t`Unnamed Item`;
  const itemBrand = item.product?.brand;
  const itemImage = item.product?.imageUrl;
  const unitLabel =
    UNIT_DISPLAY_LABELS[item.unit as keyof typeof UNIT_DISPLAY_LABELS] || item.unit || '';
  const daysUntilExpiry =
    item.earliestExpiryDate != null ? getDaysUntilExpiry(item.earliestExpiryDate) : null;

  const expiryColor =
    daysUntilExpiry === null
      ? ''
      : daysUntilExpiry < 0
        ? 'text-destructive bg-destructive/10'
        : daysUntilExpiry === 0
          ? 'text-expiring-soon bg-expiring-soon/10'
          : daysUntilExpiry <= 3
            ? 'text-expiring-soon bg-expiring-soon/10'
            : 'text-muted-foreground bg-muted/50';

  return (
    <Link
      to="/items/$itemId"
      params={{ itemId: item.id }}
      className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
    >
      {/* Product image */}
      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl overflow-hidden bg-muted/50 ring-1 ring-border/50">
        {itemImage ? (
          <img src={itemImage} alt={itemName} className="h-full w-full object-cover" />
        ) : (
          <ArchiveBoxIcon className="h-6 w-6 text-muted-foreground" />
        )}
      </div>

      {/* Name & brand */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-foreground truncate">{itemName}</p>
        {itemBrand && <p className="text-sm text-muted-foreground truncate">{itemBrand}</p>}
      </div>

      {/* Quantity + expiry */}
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <span className="text-sm font-medium text-foreground">
          {item.activeQuantity} {unitLabel}
        </span>
        {daysUntilExpiry !== null && (
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
              expiryColor
            )}
          >
            {daysUntilExpiry < 0 ? (
              <Trans>Expired</Trans>
            ) : daysUntilExpiry === 0 ? (
              <Trans>Expires today</Trans>
            ) : (
              <Trans>{daysUntilExpiry}d left</Trans>
            )}
          </span>
        )}
      </div>
    </Link>
  );
}

// ============================================================================
// GroupDetailPage
// ============================================================================

function GroupDetailPage() {
  const { t } = useLingui();
  const { groupId } = Route.useParams();
  const { selectedHousehold, isLoading: householdLoading } = useSelectedHousehold();

  const householdId = selectedHousehold?.id || '';

  const [showTargetDialog, setShowTargetDialog] = useState(false);

  const {
    data: group,
    isLoading: groupLoading,
    error: groupError,
  } = useProductGroup(householdId, groupId);

  const { data: allItems, isLoading: itemsLoading } = useItems(householdId, {});

  // Compute member items from the full items list filtered by group members
  const { memberItems, currentStock } = useMemo(() => {
    if (!group || !allItems) return { memberItems: [], currentStock: 0 };

    const memberProductIds = new Set(group.members.map((m) => m.productId));
    const filtered = allItems
      .filter((item) => item.productId != null && memberProductIds.has(item.productId))
      .sort((a, b) => {
        const aDate = a.earliestExpiryDate ?? '9999-12-31';
        const bDate = b.earliestExpiryDate ?? '9999-12-31';
        return aDate.localeCompare(bDate);
      });

    const stock = filtered.reduce((sum, item) => sum + item.activeQuantity, 0);
    return { memberItems: filtered, currentStock: stock };
  }, [group, allItems]);

  // Status computations
  const hasTarget = group?.targetQuantity != null && group.targetQuantity > 0;
  const targetMet = hasTarget && currentStock >= group!.targetQuantity!;
  const isBelowTarget = hasTarget && currentStock < group!.targetQuantity! && currentStock > 0;
  const isOutOfStock = hasTarget && currentStock === 0;
  const progressPercent = hasTarget
    ? Math.min((currentStock / group!.targetQuantity!) * 100, 100)
    : 0;

  const rawUnitLabel = group?.targetUnit
    ? UNIT_DISPLAY_LABELS[group.targetUnit as keyof typeof UNIT_DISPLAY_LABELS] || group.targetUnit
    : '';
  const unitLabel = rawUnitLabel || t({ message: 'items', context: 'unit_label' });

  // ── No household ──────────────────────────────────────────────────────────
  if (!householdLoading && !selectedHousehold) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="text-center max-w-md animate-in">
          <div className="mx-auto w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
            <HomeIcon className="h-10 w-10 text-primary" />
          </div>
          <h1 className="font-display text-3xl font-semibold text-foreground mb-3">
            <Trans>No Household Selected</Trans>
          </h1>
          <p className="text-muted-foreground mb-6">
            <Trans>Create or select a household to view group details.</Trans>
          </p>
          <Button asChild size="lg">
            <Link to="/households">
              <Trans>Manage Households</Trans>
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (householdLoading || groupLoading || itemsLoading) {
    return (
      <div className="space-y-8 pb-8">
        {/* Breadcrumb skeleton */}
        <div className="h-5 w-32 animate-pulse rounded bg-muted" />

        {/* Header skeleton */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between animate-in">
          <div className="flex gap-4">
            <div className="h-20 w-20 animate-pulse rounded-2xl bg-muted" />
            <div className="space-y-2">
              <div className="h-8 w-48 animate-pulse rounded bg-muted" />
              <div className="h-4 w-32 animate-pulse rounded bg-muted" />
              <div className="h-4 w-40 animate-pulse rounded bg-muted" />
            </div>
          </div>
        </div>

        {/* Cards skeleton */}
        <Card className="animate-in" style={{ animationDelay: '100ms' }}>
          <CardContent className="p-6">
            <div className="h-6 w-40 animate-pulse rounded bg-muted mb-4" />
            <div className="grid gap-4 sm:grid-cols-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="space-y-2">
                  <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                  <div className="h-5 w-36 animate-pulse rounded bg-muted" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (groupError) {
    return (
      <div className="space-y-6 animate-in">
        <Link
          to="/items"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <Trans>Back to Inventory</Trans>
        </Link>

        <div className="min-h-[40vh] flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center mb-4">
              <ExclamationTriangleIcon className="h-8 w-8 text-destructive" />
            </div>
            <h1 className="font-display text-2xl font-semibold text-foreground mb-2">
              <Trans>Group Not Found</Trans>
            </h1>
            <p className="text-muted-foreground mb-6">
              {(groupError as Error).message ||
                t`This group doesn't exist or you don't have access to it.`}
            </p>
            <Button asChild variant="outline">
              <Link to="/items">
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                <Trans>Back to Inventory</Trans>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!group) {
    return null;
  }

  // ── Main content ──────────────────────────────────────────────────────────
  return (
    <div className="space-y-8 pb-8">
      {/* Breadcrumb */}
      <Link
        to="/items"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors animate-in"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        <Trans>Back to Inventory</Trans>
      </Link>

      {/* Header */}
      <section
        className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between animate-in"
        style={{ animationDelay: '50ms' }}
      >
        <div className="flex gap-5">
          {/* Group icon */}
          <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-2xl overflow-hidden bg-muted/50 ring-1 ring-border/50 text-3xl">
            {group.icon ? (
              <span>{group.icon}</span>
            ) : (
              <ArchiveBoxIcon className="h-10 w-10 text-muted-foreground" />
            )}
          </div>
          <div className="space-y-1">
            <h1 className="font-display text-2xl sm:text-3xl font-semibold text-foreground">
              {group.name}
            </h1>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {group.category && (
                <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                  {group.category}
                </span>
              )}
              <span className="text-sm text-muted-foreground">
                <Plural value={group.members.length} one="# product" other="# products" />
              </span>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowTargetDialog(true)} className="h-10">
            <PencilIcon className="h-4 w-4 mr-2" />
            {hasTarget ? <Trans>Edit Target</Trans> : <Trans>Set Target</Trans>}
          </Button>
          <Button asChild variant="outline" className="h-10">
            <Link to="/households/$householdId/product-groups" params={{ householdId }}>
              <Trans>Manage Group</Trans>
            </Link>
          </Button>
        </div>
      </section>

      {/* Stock Target Card */}
      <Card className="overflow-hidden relative animate-in" style={{ animationDelay: '100ms' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
        <CardContent className="p-6 relative">
          <h2 className="font-heading font-semibold text-lg text-foreground mb-4">
            <Trans>Stock Target</Trans>
          </h2>

          {hasTarget ? (
            <div className="space-y-2">
              {/* Header row: label + current/target */}
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-medium text-foreground">
                  <Trans context="stock_status">Stock Level</Trans>
                </span>
                <span
                  className={cn(
                    'text-sm font-medium',
                    targetMet
                      ? 'text-fresh'
                      : isBelowTarget
                        ? 'text-expiring-soon'
                        : 'text-destructive'
                  )}
                >
                  {currentStock} / {group.targetQuantity} {unitLabel}
                </span>
              </div>

              {/* Progress bar */}
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className={cn(
                    'h-full rounded-full transition-all duration-500',
                    targetMet ? 'bg-fresh' : isBelowTarget ? 'bg-expiring-soon' : 'bg-destructive'
                  )}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>

              {/* Status label */}
              <p
                className={cn(
                  'text-xs',
                  targetMet
                    ? 'text-fresh'
                    : isBelowTarget
                      ? 'text-expiring-soon'
                      : 'text-destructive'
                )}
              >
                {targetMet ? (
                  <Trans context="stock_status">Target met</Trans>
                ) : isOutOfStock ? (
                  <Trans context="stock_status">Out of stock</Trans>
                ) : (
                  <Trans context="stock_status">
                    Below target — {group.targetQuantity! - currentStock} {unitLabel} needed
                  </Trans>
                )}
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-start gap-4">
              <p className="text-muted-foreground">
                <Trans>No stock target set for this group.</Trans>
              </p>
              <Button variant="outline" onClick={() => setShowTargetDialog(true)}>
                <PencilIcon className="h-4 w-4 mr-2" />
                <Trans context="action_button">Set Target</Trans>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Member Items Card */}
      <Card className="overflow-hidden relative animate-in" style={{ animationDelay: '150ms' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
        <CardContent className="p-6 relative">
          <h2 className="font-heading font-semibold text-lg text-foreground mb-4">
            <Trans>Items in this Group</Trans>
          </h2>

          {memberItems.length > 0 ? (
            <div className="divide-y divide-border/50">
              {memberItems.map((item) => (
                <MemberItemRow key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="mx-auto w-12 h-12 rounded-2xl bg-muted flex items-center justify-center mb-3">
                <ArchiveBoxIcon className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">
                <Trans>No items in your inventory belong to this group yet.</Trans>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Set Target Dialog */}
      <SetTargetDialog
        open={showTargetDialog}
        onOpenChange={setShowTargetDialog}
        mode="group"
        entityId={groupId}
        entityName={group.name}
        householdId={householdId}
        currentQuantity={group.targetQuantity}
        currentUnit={group.targetUnit}
      />
    </div>
  );
}
